import React, { useEffect, useState } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { editorConfig } from '../../../config/ckeditorConfig';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useUpdateCms } from '../api/cmsapi';
import { useToast } from '../../../hooks/usetoast';
import { WaitingLoader } from '../../../hooks/buttonLoad';
import { queryClient } from '../../../config';
import PropTypes from 'prop-types';
import { Avatar } from '@mui/material';
import { stringAvatar } from '../../../lib/avatar';

function Cms({ editData, toggleEdit }) {
    // Define validation schema
    const schema = yup.object().shape({
        ...(editData?.title === "Features" && {
            image: yup.mixed()
                .test("required", "Image is required", file => !!file)
                .required("Image is required"),
        }),
        linkTitle: yup.string().required('Required'),
        content: yup.string().required('Required'),
    });

    const [profileimg, setProfileimg] = useState('');

    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
        setError,
        setValue,
        clearErrors,
        register,
    } = useForm({
        mode: 'onBlur',
        resolver: yupResolver(schema),
        defaultValues: {
            linkTitle: '',
            content: '',
        }
    });

    const [isloading, setIsloading] = useState(false);

    const updateCms = useUpdateCms();
    const toast = useToast();

    // Reset form data when editData changes
    useEffect(() => {
        if (editData) {
            // Reset form and set values for each field
            reset({
                linkTitle: editData.heading || '',
                content: editData.content || '',
                image: editData.image || ''
            });
            // Set the profile image
            setProfileimg(editData.image || '');
        }
    }, [editData, reset]);

    // Helper function for reading image files
    const readFileAsync1 = (file) => {
        return new Promise((resolve, reject) => {
            if (!file) {
                return;
            }
            const reader = new FileReader();
            reader.onload = () => {
                const base64Data = btoa(reader.result);
                resolve(`data:${file.type};base64,${base64Data}`);
            };
            reader.onerror = reject;
            reader.readAsArrayBuffer(file);
        });
    };

    const handleProfile = (event) => {
        let img = event.target.files[0];
        const reader = new FileReader();

        reader.onload = (event) => {
            const imgCheck = new Image();
            imgCheck.src = event.target.result;
            imgCheck.onload = async () => {
                if (imgCheck.width <= 6000 && imgCheck.height <= 6000) {
                    if (img.size < 2 * 1024 * 1024) {
                        await readFileAsync1(img);
                        setProfileimg(img);
                        setValue("image", img); // Set value correctly
                    } else {
                        setError("image", { message: "Image Size Must be Less than 2MB." });
                    }
                } else {
                    setError("image", { message: "Image Dimensions should be 6000 x 6000 Pixels." });
                }
            };

            imgCheck.onerror = () => {
                setError("image", { message: "Invalid image file." });
            };
        };
        reader.onerror = (error) => { };
        reader.readAsDataURL(img);
    };

    const onSubmit = async (data) => {
        setIsloading(true);
        const formData = new FormData();
        formData.append("id", editData?._id);
        formData.append("linkTitle", data.linkTitle);
        formData.append("image", data.image ? data.image : '');
        formData.append("content", data.content);

        const response = await updateCms.mutateAsync(formData);

        if (response) {
            setIsloading(false);
            if (response.status) {
                toast.success(response?.data?.message, {
                    showOnlyTheLastOne: true,
                    clickToClose: true,
                    timeout: 2000,
                });
                toggleEdit();
                queryClient.refetchQueries("cms");
            } else {
                toast.error(response?.data?.message || "Something went wrong", {
                    showOnlyTheLastOne: true,
                    clickToClose: true,
                    timeout: 2000,
                });
            }
        }
    };

    return (
        <>
            {isloading && <WaitingLoader />}
            <form onSubmit={handleSubmit(onSubmit)} className='minW50'>
                <div className='p-1' style={{ width: "400px" }}>
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                        <h4>{editData?.title}</h4>
                    </div>

                    {editData?.title === "Features" && (
                        <>
                            <div className="d-flex justify-content-center">
                                <Avatar {...stringAvatar('Admin')}
                                    src={profileimg || editData?.image}
                                    sx={{ width: 150, height: 150 }}
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label" htmlFor="image">Image</label>
                                <input
                                    name='image' className="form-control"
                                    accept='.jpg,.png,.jpeg'
                                    type="file" id="formFileMultiple1"
                                    onChange={async (e) => {
                                        try {
                                            let img = e.target.files[0];
                                            const reader = new FileReader();
                                            clearErrors("image");
                                            setValue("image", null);
                                            setProfileimg(null);
                                            reader.onload = (event) => {
                                                const bytes = new Uint8Array(event.target.result);
                                                if ((bytes[0] === 0x89 && bytes[1] === 0x50) || (bytes[0] === 0xFF && bytes[1] === 0xD8)) {
                                                    handleProfile(e);
                                                } else {
                                                    setError("image", { message: 'Please select image file' });
                                                }
                                            };
                                            reader.readAsArrayBuffer(img);
                                        } catch (e) { }
                                    }} />
                                {errors.image && <p className='errMsg'>{errors.image.message}</p>}
                            </div>
                        </>
                    )}

                    {/* Heading Field */}
                    <div className='mb-3'>
                        <label className="form-label" htmlFor="linkTitle">Title</label>
                        <div className="input-group">
                            <input
                                type="text"
                                className="form-control"
                                {...register('linkTitle')}
                                autoComplete='off'
                            />
                        </div>
                        {errors.linkTitle && <span className="text-danger">{errors.linkTitle.message}</span>}
                    </div>

                    {/* Content Field */}
                    <div className='mb-3'>
                        <label className="form-label" htmlFor="content">Content</label>

                        <Controller
                            name="content"
                            control={control}
                            render={({ field }) => (
                                <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #ccc', padding: '0px' }}>
                                    <CKEditor
                                        editor={ClassicEditor}
                                        config={editorConfig}
                                        data={field.value}
                                        onChange={(event, editor) => {
                                            const data = editor.getData();
                                            field.onChange(data);
                                        }}
                                        s
                                    />
                                </div>
                            )}
                        />

                        {errors.content && <span className="text-danger">{errors.content.message}</span>}
                    </div>

                    <div className='d-flex justify-content-center'>
                        <button type='submit' className='btn btn-primary' disabled={isloading}>
                            Update
                        </button>
                    </div>
                </div>
            </form>
        </>
    );
}

Cms.propTypes = {
    editData: PropTypes.object,
    toggleEdit: PropTypes.func,
};

export default Cms;









